import React, { useMemo, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Toolbar } from './src/components/Toolbar';
import { FilterSidebar } from './src/components/FilterSidebar';
import { DataTable } from './src/components/DataTable';
import { Header } from './src/components/Header';
import { DetailDrawer } from './src/components/DetailDrawer';
import { ComparisonView } from './src/components/ComparisonView';
import { UploadIntake } from './src/components/UploadIntake';
import { LoginScreen } from './src/components/LoginScreen';
import { mockData, PARTIES, DiscoveryResponse, ResponseStatus } from './src/data';
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Filter states
  const [selectedParties, setSelectedParties] = useState<string[]>(PARTIES);
  const [selectedSets, setSelectedSets] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<ResponseStatus[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState<DiscoveryResponse | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  // View state
  const [currentView, setCurrentView] = useState<'workspace' | 'upload' | 'comparison'>('workspace');
  const [comparisonInterrogatory, setComparisonInterrogatory] = useState<DiscoveryResponse | null>(null);
  // Sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: keyof DiscoveryResponse;
    direction: 'asc' | 'desc';
  } | null>({
    key: 'party',
    direction: 'asc'
  });
  const handleSort = (key: keyof DiscoveryResponse) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({
      key,
      direction
    });
  };
  const handleCompare = (row: DiscoveryResponse) => {
    setSelectedRow(null);
    setComparisonInterrogatory(row);
    setCurrentView('comparison');
  };
  const handleBackToWorkspace = () => {
    setComparisonInterrogatory(null);
    setCurrentView('workspace');
  };
  const hasActiveFilters = selectedSets.length > 0 || selectedTypes.length > 0 || selectedStatuses.length > 0 || searchQuery.trim() !== '' || selectedParties.length !== PARTIES.length;
  const handleReset = () => {
    setSelectedParties(PARTIES);
    setSelectedSets([]);
    setSelectedTypes([]);
    setSelectedStatuses([]);
    setSearchQuery('');
  };
  const filteredData = useMemo(() => {
    return mockData.filter(item => {
      if (selectedParties.length > 0 && !selectedParties.includes(item.party)) return false;
      if (selectedSets.length > 0 && !selectedSets.includes(item.set)) return false;
      if (selectedTypes.length > 0 && !selectedTypes.includes(item.type)) return false;
      if (selectedStatuses.length > 0) {
        const hasMatchingStatus = item.statuses.some(status => selectedStatuses.includes(status));
        if (!hasMatchingStatus) return false;
      }
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        const searchableText = `${item.question} ${item.response} ${item.number} ${item.party}`.toLowerCase();
        if (!searchableText.includes(query)) return false;
      }
      return true;
    });
  }, [selectedParties, selectedSets, selectedTypes, selectedStatuses, searchQuery]);
  const sortedData = useMemo(() => {
    const sortableItems = [...filteredData];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (Array.isArray(aValue)) aValue = aValue.join(', ');
        if (Array.isArray(bValue)) bValue = bValue.join(', ');
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredData, sortConfig]);
  if (!isAuthenticated) {
    return <LoginScreen onLogin={() => setIsAuthenticated(true)} data-id="element-0" />;
  }
  return <div className="flex flex-col h-screen w-full overflow-hidden bg-geniac-bg font-sans text-geniac-text" data-id="element-1">
      <Header totalCount={mockData.length} filteredCount={filteredData.length} onUploadClick={() => setCurrentView('upload')} data-id="element-2" />

      {currentView === 'upload' ? <div className="flex-1 overflow-hidden" data-id="element-3">
          <UploadIntake onBack={handleBackToWorkspace} data-id="element-4" />
        </div> : currentView === 'comparison' && comparisonInterrogatory ? <div className="flex-1 overflow-hidden" data-id="element-5">
          <ComparisonView interrogatory={comparisonInterrogatory} onBack={handleBackToWorkspace} data-id="element-6" />
        </div> : <>
          <Toolbar filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} hasActiveFilters={hasActiveFilters} data-id="element-7" />
          <div className="flex-1 overflow-hidden flex" data-id="element-8">
            <AnimatePresence data-id="element-9">
              {filtersOpen && <FilterSidebar selectedParties={selectedParties} setSelectedParties={setSelectedParties} selectedSets={selectedSets} setSelectedSets={setSelectedSets} selectedTypes={selectedTypes} setSelectedTypes={setSelectedTypes} selectedStatuses={selectedStatuses} setSelectedStatuses={setSelectedStatuses} searchQuery={searchQuery} setSearchQuery={setSearchQuery} onReset={handleReset} hasActiveFilters={hasActiveFilters} data-id="element-10" />}
            </AnimatePresence>

            <main className="flex-1 overflow-hidden bg-geniac-bg flex flex-col min-w-0" data-id="element-11">
              <DataTable data={sortedData} sortConfig={sortConfig} onSort={handleSort} onRowClick={setSelectedRow} selectedRowId={selectedRow?.id} data-id="element-12" />
            </main>

            <AnimatePresence data-id="element-13">
              {selectedRow && <DetailDrawer row={selectedRow} onClose={() => setSelectedRow(null)} onCompare={handleCompare} data-id="element-14" />}
            </AnimatePresence>
          </div>
        </>}
    </div>;
}